//* Libraries imports
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tab,
  Tabs,
  TextField,
  Alert,
} from "@mui/material";
import {
  DownloadIcon,
  TrashIcon,
  Upload as UploadIcon,
  FileText,
  Search,
} from "lucide-react";

//* App imports
import { AppDispatch, RootState } from "@/app/store";

//* Component imports
import { LoadingSkeleton } from "@/components/ui/LoadingSkeleton";
import { DataTable } from "@/components/ui";
import { Column } from "@/components/ui/DataTable";
import { DeleteConfirmationAlert } from "./DeleteConfirmationAlert";

//* Features imports
import { fetchClients } from "@/features/Client";
import { ClientOutputDTO } from "@/features/Client/types";
import { fetchPhaseForms } from "@/features/phaseForm";
import { PhaseFormOutputDTO } from "@/features/phaseForm/types";
import { MappingRule } from "@/features/template/types";

//* Hooks imports
import {
  useProcessMapping,
  FilledForm,
  HistoryItem,
} from "@/hooks/processes/querys/useProcessMapping";

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

function CustomTabPanel(props: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={props.value !== props.index}>
      {props.value === props.index && <Box sx={{ p: 3 }}>{props.children}</Box>}
    </div>
  );
}

const getMappingValue = (
  item: MappingRule,
  client: ClientOutputDTO | undefined,
  phaseFormFilledRef: Record<string, FilledForm>,
): string | number | boolean | null => {
  if (!client) return "";

  if (item.type === "client") {
    const field = item.config.field;
    if (field === "cpf") return client.cpf || "";
    if (field === "name") return client.name || "";
    if (field === "email") return client.email || "";
    if (field === "full_address") {
      const place = client.places?.[0];
      return place
        ? `${place.street}, ${place.number}, ${place.neighborhood} - ${place.cityName}/${place.stateCode}`
        : "";
    }
    if (field === "whatsapp") return client.whatsapp || "";
  }

  if (
    item.type === "form" &&
    item.config.phaseFormId &&
    phaseFormFilledRef[item.config.phaseFormId]
  ) {
    const filledForm = phaseFormFilledRef[item.config.phaseFormId];
    const fieldData = filledForm.data.find(
      (d) => d.field._id === item.config.fieldId,
    );

    if (fieldData) {
      const rawValue = fieldData.data;
      if (
        item.config.fieldType === "SELECT" &&
        Array.isArray(item.config.items)
      ) {
        const mapVal = (val: any): string => {
          const opt = fieldData.field.options?.find(
            (o) =>
              String(o.value) === String(val) ||
              (o.key && String(o.key) === String(val)),
          );
          const key = opt ? opt.key || opt.value : String(val);
          const selected = item.config.items?.find(
            (s: any) => s.key === key || s.value === key,
          );
          return selected ? selected.value : key;
        };
        return Array.isArray(rawValue)
          ? rawValue.map(mapVal).join(", ")
          : mapVal(rawValue);
      }
      return String(rawValue);
    }
  }

  return item.type === "fixed" ? item.config.value || "" : "";
};

type MappingRuleDisplay = MappingRule & {
  filled?: string;
  value?: string;
};

export type ApplyTagsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  entity: {
    _id: string;
    name: string;
    config?: {
      mappings?: MappingRule[];
    };
  };
  onGenerate: (
    replacements: Record<string, string | number | boolean | null>,
    mappingRules: MappingRule[],
    clientId?: string,
  ) => Promise<void>;
  onUpload?: (file: File, clientId: string) => Promise<void>;
  history?: {
    items: HistoryItem[];
    loading: boolean;
    onDelete: (id: string) => Promise<void>;
    onFetch: (filters: {
      clientId?: string;
      type?: "automatic" | "upload";
      startDate?: string;
      endDate?: string;
    }) => void;
  };
  isGenerating?: boolean;
  title?: string;
  acceptedFileTypes?: string;
  clientId?: string;
};

export default function ApplyTagsModal(props: ApplyTagsModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [tabValue, setTabValue] = useState<number>(0);
  const [selectedClient, setSelectedClient] = useState<string | null>(
    props.clientId || null,
  );
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const today = new Date().toISOString().split("T")[0];

  const [historyFilters, setHistoryFilters] = useState<{
    type: "automatic" | "upload" | "";
    startDate: string;
    endDate: string;
  }>({
    type: "",
    startDate: "",
    endDate: today,
  });

  const processMapping = useProcessMapping();

  const clientState = useSelector((state: RootState) => state.client);
  const clientList = Array.isArray(clientState.items)
    ? clientState.items
    : (clientState.items as any).data || [];
  const phaseForms = useSelector(
    (state: RootState) => state.phaseForm.items,
  ) as PhaseFormOutputDTO[];

  useEffect(() => {
    if (props.isOpen) {
      dispatch(fetchPhaseForms());
      dispatch(fetchClients({}));
      if (props.clientId) {
        setSelectedClient(props.clientId);
        processMapping.loadProcessClient(
          props.clientId,
          clientList,
          props.entity.config?.mappings,
        );
        props.history?.onFetch?.({
          clientId: props.clientId,
          type: historyFilters.type || undefined,
          startDate: historyFilters.startDate || undefined,
          endDate: historyFilters.endDate || undefined,
        });
      }
    } else {
      setSelectedClient(null);
      setTabValue(0);
      setErrorMessage(null);
    }
  }, [props.isOpen, dispatch, props.clientId]);

  const handleGenerate = async () => {
    const client = clientList.find((c: any) => c._id === selectedClient);
    if (!client) return;

    const replacements: Record<string, any> = {};
    props.entity.config?.mappings?.forEach((m) => {
      replacements[m.tag] = getMappingValue(
        m,
        client,
        processMapping.phaseFormFilledRef,
      );
    });

    await props.onGenerate(
      replacements,
      props.entity.config?.mappings || [],
      selectedClient || undefined,
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(null);
    const file = e.target.files?.[0];
    if (!file || !selectedClient || !props.onUpload) return;

    if (props.acceptedFileTypes) {
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      const acceptedTypes = props.acceptedFileTypes
        .toLowerCase()
        .split(",")
        .map((t) => t.trim());

      if (!acceptedTypes.includes(fileExtension)) {
        setErrorMessage(
          `Por favor, selecione um arquivo do tipo: ${props.acceptedFileTypes}`,
        );
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    try {
      processMapping.setIsLoadingData(true);
      await props.onUpload(file, selectedClient);
      props.history?.onFetch?.({
        clientId: selectedClient,
        type: historyFilters.type || undefined,
        startDate: historyFilters.startDate || undefined,
        endDate: historyFilters.endDate || undefined,
      });
    } finally {
      processMapping.setIsLoadingData(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const columns_filled: Column<MappingRuleDisplay>[] = [
    { key: "tag", header: "Tag", render: (_, i) => i.tag },
    {
      key: "type",
      header: "Tipo",
      render: (_, i) =>
        i.type === "client"
          ? "Dados do cliente"
          : i.type === "form"
            ? "Dados do formuário"
            : "Valor fixo",
    },
    {
      key: "config",
      header: "Dado",
      render: (_, i) => {
        if (i.type === "client") {
          const fieldMap: Record<string, string> = {
            cpf: "CPF",
            name: "Nome",
            email: "E-mail",
            full_address: "Endereço completo",
            whatsapp: "Whatsapp/Celular",
          };
          return fieldMap[i.config.field || ""] || "";
        }
        return i.type === "form"
          ? phaseForms.find((f) => f._id === i.config.phaseFormId)?.name || ""
          : i.config.value || "";
      },
    },
    {
      key: "filled",
      header: "Preenchido",
      render: (_, i) => {
        const isNotFilled =
          i.type === "form"
            ? processMapping.notFoundFormRef[i.config.phaseFormId!]
            : i.type === "client"
              ? processMapping.completedClientRef[i.config.field!] !== undefined
              : !i.config.value;
        return isNotFilled ? "Não" : "Sim";
      },
    },
    {
      key: "value",
      header: "Valor",
      render: (_, i) =>
        String(
          getMappingValue(
            i,
            clientList.find((c: any) => c._id === selectedClient),
            processMapping.phaseFormFilledRef,
          ) || "",
        ),
    },
  ];

  const columns_history: Column<HistoryItem>[] = [
    {
      key: "createdAt",
      header: "Data",
      render: (_, i) => moment(i.createdAt).format("DD/MM/YYYY HH:mm"),
    },
    {
      key: "type",
      header: "Tipo",
      render: (_, i) => {
        const isSystem = i.type === "automatic";
        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isSystem
                ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                : "bg-amber-100 text-amber-700 border border-amber-200"
            }`}
          >
            {isSystem ? "Sistema" : "Manual"}
          </span>
        );
      },
    },
    {
      key: "clientId",
      header: "Cliente",
      render: (_, i) =>
        clientList.find((c: any) => c._id === i.clientId)?.name ||
        i.clientId ||
        "-",
    },
  ];
  if (!props.isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 items-center justify-center p-4 custom-scrollbar"
        style={{ zIndex: 1000, overflowY: "scroll" }}
        onClick={props.onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-xl p-6 flex flex-col gap-4"
          style={{ minWidth: "70%" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center text-xl">
            <b>{props.title || `Aplicar "${props.entity?.name}" em cliente`}</b>
          </div>

          <div>
            {!props.clientId && (
              <FormControl fullWidth size="small">
                <InputLabel>Selecione um cliente</InputLabel>
                <Select
                  className="h-12 rounded-xl border-gray-200"
                  value={selectedClient || ""}
                  onChange={(e) => {
                    const id = e.target.value as string;
                    setSelectedClient(id);
                    processMapping.loadProcessClient(
                      id,
                      clientList,
                      props.entity.config?.mappings,
                    );
                    props.history?.onFetch?.({
                      clientId: id,
                      type: historyFilters.type || undefined,
                      startDate: historyFilters.startDate || undefined,
                      endDate: historyFilters.endDate || undefined,
                    });
                  }}
                  label="Selecione um cliente"
                >
                  {clientList.map((c: any) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>

          {selectedClient && (
            <Box sx={{ width: "100%" }}>
              {props.history && (
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                    <Tab label="Mapeamento" />
                    <Tab label="Histórico" />
                  </Tabs>
                </Box>
              )}

              {processMapping.isLoadingData ? (
                <Box sx={{ p: 3 }}>
                  <LoadingSkeleton title={false} filter={false} />
                </Box>
              ) : (
                <>
                  <CustomTabPanel value={tabValue} index={0}>
                    <div className="flex flex-col gap-4">
                      <div className="flex justify-between items-center">
                        <p>
                          <b>Mapeamento: </b>
                        </p>
                        {props.onUpload && (
                          <>
                            <input
                              type="file"
                              ref={fileInputRef}
                              accept={props.acceptedFileTypes}
                              onChange={handleFileChange}
                              style={{ display: "none" }}
                              title="Upload de arquivo"
                              placeholder="Selecione um arquivo"
                            />
                            <button
                              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium text-sm"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <UploadIcon size={16} /> Upload Manual
                            </button>
                          </>
                        )}
                      </div>
                      {errorMessage && (
                        <Alert
                          severity="error"
                          onClose={() => setErrorMessage(null)}
                        >
                          {errorMessage}
                        </Alert>
                      )}
                      <div className="grid grid-cols-1 p-2">
                        <DataTable
                          data={
                            props.entity.config?.mappings?.map((r, idx) => ({
                              ...r,
                              _id: r.tag || String(idx),
                            })) || []
                          }
                          columns={columns_filled}
                          newFormat
                          basePath="filled-forms"
                          hide_add
                          hide_search
                          hide_header
                          hideEdit
                          hide_actions={true}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={props.onClose}
                          className="px-8 py-3 bg-red-500 text-white rounded-lg"
                        >
                          Fechar
                        </button>
                        <button
                          onClick={handleGenerate}
                          disabled={props.isGenerating}
                          className={`flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors ${
                            props.isGenerating ? "opacity-50" : ""
                          }`}
                        >
                          {props.isGenerating ? (
                            "Gerando..."
                          ) : (
                            <>
                              <FileText size={20} />
                              Gerar Documento
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </CustomTabPanel>
                  {props.history && (
                    <CustomTabPanel value={tabValue} index={1}>
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-4 mb-4">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <FormControl fullWidth size="small">
                              <InputLabel>Tipo</InputLabel>
                              <Select
                                value={historyFilters.type}
                                label="Tipo"
                                onChange={(e) =>
                                  setHistoryFilters((prev) => ({
                                    ...prev,
                                    type: e.target.value as any,
                                  }))
                                }
                              >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="automatic">Sistema</MenuItem>
                                <MenuItem value="upload">Manual</MenuItem>
                              </Select>
                            </FormControl>

                            <TextField
                              fullWidth
                              size="small"
                              label="Data Início"
                              type="date"
                              slotProps={{ inputLabel: { shrink: true } }}
                              inputProps={{ max: today }}
                              value={historyFilters.startDate}
                              onChange={(e) =>
                                setHistoryFilters((prev) => ({
                                  ...prev,
                                  startDate: e.target.value,
                                }))
                              }
                            />

                            <TextField
                              fullWidth
                              size="small"
                              label="Data Fim"
                              type="date"
                              slotProps={{ inputLabel: { shrink: true } }}
                              inputProps={{ max: today }}
                              value={historyFilters.endDate}
                              onChange={(e) =>
                                setHistoryFilters((prev) => ({
                                  ...prev,
                                  endDate: e.target.value,
                                }))
                              }
                            />

                            <button
                              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium h-[40px]"
                              onClick={() => {
                                if (selectedClient) {
                                  props.history?.onFetch?.({
                                    clientId: selectedClient,
                                    type: historyFilters.type || undefined,
                                    startDate:
                                      historyFilters.startDate || undefined,
                                    endDate:
                                      historyFilters.endDate || undefined,
                                  });
                                }
                              }}
                            >
                              <Search size={16} /> Filtrar
                            </button>
                          </div>
                        </div>

                        <DataTable
                          data={props.history.items}
                          columns={columns_history}
                          newFormat
                          basePath="history"
                          hide_add
                          hide_search
                          hide_header
                          hideEdit
                          loading={props.history.loading}
                          hide_actions={false}
                          actions={[
                            {
                              label: "Baixar",
                              icon: <DownloadIcon size={16} />,
                              onClick: (item) => {
                                const link = document.createElement("a");
                                link.href = item.url || item.documentUrl || "";
                                link.target = "_blank";
                                link.download = "historico.pdf";
                                link.click();
                              },
                            },
                            {
                              label: "Excluir",
                              icon: <TrashIcon size={16} />,
                              onClick: (item) => setDeleteId(item._id),
                            },
                          ]}
                        />
                      </div>
                    </CustomTabPanel>
                  )}
                </>
              )}
            </Box>
          )}
          {!selectedClient && (
            <div className="flex justify-end gap-2">
              <button
                onClick={props.onClose}
                className="px-8 py-3 bg-red-500 text-white rounded-lg"
              >
                Fechar
              </button>
              <button
                disabled
                className={`flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors ${"opacity-50"}`}
              >
                {props.isGenerating ? (
                  "Gerando..."
                ) : (
                  <>
                    <FileText size={20} />
                    Gerar Documento
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmationAlert
        isOpen={!!deleteId}
        itemName="este registro do histórico"
        onCancel={() => setDeleteId(null)}
        onConfirm={async () => {
          if (deleteId) {
            await props.history?.onDelete(deleteId);
            if (selectedClient) {
              props.history?.onFetch?.({ clientId: selectedClient });
            }
            setDeleteId(null);
          }
        }}
      />
    </>
  );
}
